import { createContext, useContext, useState, type ReactNode } from 'react'

interface AppState {
  familyId: string | null
  token: string | null
  role: 'parent' | 'child' | null
  playerId: string | null
  playerName: string | null
}

interface AppContextType {
  state: AppState
  setAuth: (familyId: string, token: string) => void
  setRole: (role: 'parent' | 'child') => void
  setPlayer: (id: string, name: string) => void
  logout: () => void
}

const AppContext = createContext<AppContextType | null>(null)

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => ({
    familyId: localStorage.getItem('family_id'),
    token: localStorage.getItem('auth_token'),
    role: null,
    playerId: null,
    playerName: null,
  }))

  const setAuth = (familyId: string, token: string) => {
    localStorage.setItem('family_id', familyId)
    localStorage.setItem('auth_token', token)
    setState((s) => ({ ...s, familyId, token }))
  }

  const setRole = (role: 'parent' | 'child') => {
    setState((s) => ({ ...s, role }))
  }

  const setPlayer = (id: string, name: string) => {
    setState((s) => ({ ...s, playerId: id, playerName: name }))
  }

  const logout = () => {
    localStorage.removeItem('family_id')
    localStorage.removeItem('auth_token')
    setState({ familyId: null, token: null, role: null, playerId: null, playerName: null })
  }

  return (
    <AppContext.Provider value={{ state, setAuth, setRole, setPlayer, logout }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}
